"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"

import {
  Building2,
  TrendingUp,
  TrendingDown,
  Activity,
  Award,
  Users,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react"
import { useAppContext } from "../../../shared/functions/Context"
import type { IUser } from "../../../shared/models/User"
import { calculateUserScores } from "./user-scoring"


interface RaterData {
  ratings: {
    [key: string]: { [key: string]: number | number[] }
  }
  stopDoing: string
  startDoing: string
  continueDoing: string
}

interface GroupedRating {
  rateeId: string
  raterId: string
  raterData: RaterData
  date: string
}

interface GroupedRatings {
  [rateeId: string]: GroupedRating[]
}

interface DepartmentRatings {
  values: { [departmentName: string]: number }
  leadership: { [departmentName: string]: number }
  template: { [departmentName: string]: number }
  final: { [departmentName: string]: number }
}

interface UserScoreData {
  userId: string
  userName: string
  values: number
  leadership: number
  templates: number
}

interface DepartmentScoreData {
  values: number[]
  leadership: number[]
  templates: number[]
  userCount: number
  users: UserScoreData[]
}

interface PaginationState {
  [departmentName: string]: number
}

interface ExpandedState {
  [departmentName: string]: boolean
}

const Department = observer(() => {
  const { api, store } = useAppContext()
  const [loading, setLoading] = useState(true)
  const [highestRatedDepartments, setHighestRatedDepartments] = useState<string[]>([])
  const [lowestRatedDepartments, setLowestRatedDepartments] = useState<string[]>([])
  const [departmentAverages, setDepartmentAverages] = useState<DepartmentRatings>({
    values: {},
    leadership: {},
    template: {},
    final: {},
  })
  const [departmentUserScores, setDepartmentUserScores] = useState<{
    [dept: string]: UserScoreData[]
  }>({})
  const [pagination, setPagination] = useState<PaginationState>({})
  const [expandedDepts, setExpandedDepts] = useState<ExpandedState>({})
  const itemsPerPage = 5

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([api.user.getAll(), api.department.getAll(), api.ratingAssignments.getAll()])
        const users = store.user.all.map((user) => user.asJson)
        const description = store.ratingAssignments.all.find((rate) => rate.asJson.status === "In Progress")?.asJson
          .description

        if (description && users.length) {
          const uids = store.user.all.map((user) => user.asJson.uid)
          await Promise.all([
            api.valueRating.getAllNew(uids, description),
            api.leadershipRating.getAllNew(uids, description),
            api.templateRating.getAll(),
          ])
        }
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [api, store])

  useEffect(() => {
    if (!loading) {
      const calculateDepartmentAverages = () => {
        const valueRatings = store.valueRating.all.map((rating) => rating.asJson)
        const leadershipRatings = store.leadershipRating.all.map((rating) => rating.asJson)
        const templateRatings = store.templateRating.all.map((rating) => rating.asJson)
        const users = store.user.all.map((user) => user.asJson)

        const departmentScores: {
          [dept: string]: DepartmentScoreData
        } = {}

        users.forEach((user) => {
          const userScores = calculateUserScores(user.uid, user, valueRatings, leadershipRatings, templateRatings)
          const deptName = getDepartmentName(user.department)

          if (deptName) {
            if (!departmentScores[deptName]) {
              departmentScores[deptName] = { values: [], leadership: [], templates: [], userCount: 0, users: [] }
            }
            departmentScores[deptName].values.push(userScores.values)
            departmentScores[deptName].leadership.push(userScores.leadership)
            departmentScores[deptName].templates.push(userScores.templates)
            departmentScores[deptName].userCount += 1
            departmentScores[deptName].users.push({
              userId: user.uid,
              userName: userScores.userName,
              values: userScores.values,
              leadership: userScores.leadership,
              templates: userScores.templates,
            })

            console.log(
              `[v0] Department Average - User: ${userScores.userName}, Department: ${deptName}, Values: ${userScores.values}, Leadership: ${userScores.leadership}, Templates: ${userScores.templates}`,
            )
          }
        })

        const valueAverages: { [dept: string]: number } = {}
        const leadershipAverages: { [dept: string]: number } = {}
        const templateAverages: { [dept: string]: number } = {}

        Object.entries(departmentScores).forEach(([dept, scores]) => {
          const userCount = scores.userCount

          const valueSum = scores.values.reduce((a, b) => a + b, 0)
          const leadershipSum = scores.leadership.reduce((a, b) => a + b, 0)
          const templateSum = scores.templates.reduce((a, b) => a + b, 0)

          valueAverages[dept] = userCount > 0 ? valueSum / userCount : 0
          leadershipAverages[dept] = userCount > 0 ? leadershipSum / userCount : 0
          templateAverages[dept] = userCount > 0 ? templateSum / userCount : 0

          console.log(`--------------------------------------------------`)
          console.log(`[v0] Department: ${dept}`)
          console.log(`[v0] Total Users: ${userCount}`)
          console.log(
            `[v0] Values Sum: ${valueSum.toFixed(2)} ÷ ${userCount} = Average: ${valueAverages[dept].toFixed(2)}`,
          )
          console.log(
            `[v0] Leadership Sum: ${leadershipSum.toFixed(2)} ÷ ${userCount} = Average: ${leadershipAverages[dept].toFixed(2)}`,
          )
          console.log(
            `[v0] Templates Sum: ${templateSum.toFixed(2)} ÷ ${userCount} = Average: ${templateAverages[dept].toFixed(2)}`,
          )
        })

        const finalAverages = calculateFinalDepartmentRatings(valueAverages, leadershipAverages, templateAverages)

        setDepartmentAverages({
          values: valueAverages,
          leadership: leadershipAverages,
          template: templateAverages,
          final: finalAverages,
        })

        const userScoresByDept: { [dept: string]: UserScoreData[] } = {}
        Object.entries(departmentScores).forEach(([dept, data]) => {
          userScoresByDept[dept] = data.users
        })
        setDepartmentUserScores(userScoresByDept)

        const departmentNames = Object.keys(finalAverages)
        if (departmentNames.length > 0) {
          const highestRated = departmentNames.reduce((highest, current) => {
            return finalAverages[current] > finalAverages[highest] ? current : highest
          }, departmentNames[0])

          const lowestRated = departmentNames.reduce((lowest, current) => {
            return finalAverages[current] < finalAverages[lowest] ? current : lowest
          }, departmentNames[0])

          setHighestRatedDepartments([highestRated])
          setLowestRatedDepartments([lowestRated])
        }
      }

      calculateDepartmentAverages()
    }
  }, [loading, store.valueRating.all, store.leadershipRating.all, store.templateRating.all, store.user.all])

  const calculateFinalDepartmentRatings = (
    valueAverages: { [departmentName: string]: number },
    leadershipAverages: { [departmentName: string]: number },
    templateAverages: { [departmentName: string]: number },
  ): { [departmentName: string]: number } => {
    const finalRatings: { [departmentName: string]: number } = {}

    const allDepartments = new Set([
      ...Object.keys(valueAverages),
      ...Object.keys(leadershipAverages),
      ...Object.keys(templateAverages),
    ])

    allDepartments.forEach((dept) => {
      const values = valueAverages[dept] || 0
      const leadership = leadershipAverages[dept] || 0
      const template = templateAverages[dept] || 0

      finalRatings[dept] = values + leadership + template

      console.log(
        `[v0] Final Rating for ${dept}: ${values.toFixed(2)} + ${leadership.toFixed(2)} + ${template.toFixed(2)} = ${finalRatings[dept].toFixed(2)}`,
      )
    })

    return finalRatings
  }

  const getUserDetails = (userId: string): IUser | null => {
    const user = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
    return user || null
  }

  const getDepartmentName = (departmentId: string): string => {
    const department = store.department.all.find((department) => department.asJson.id === departmentId)?.asJson
    return department ? (department.name ?? "") : ""
  }

  const handleNextPage = (departmentName: string, totalUsers: number) => {
    const maxPage = Math.ceil(totalUsers / itemsPerPage) - 1
    setPagination((prev) => ({
      ...prev,
      [departmentName]: Math.min((prev[departmentName] || 0) + 1, maxPage),
    }))
  }

  const handlePrevPage = (departmentName: string) => {
    setPagination((prev) => ({
      ...prev,
      [departmentName]: Math.max((prev[departmentName] || 0) - 1, 0),
    }))
  }

  const getPaginatedUsers = (users: UserScoreData[], departmentName: string) => {
    const currentPage = pagination[departmentName] || 0
    const startIndex = currentPage * itemsPerPage
    return users.slice(startIndex, startIndex + itemsPerPage)
  }

  // Added ExpandedState interface and handleToggleExpand function
  const handleToggleExpand = (departmentName: string) => {
    setExpandedDepts((prev) => ({
      ...prev,
      [departmentName]: !prev[departmentName],
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  const totalDepartments = Object.keys(departmentAverages.final).length
  const isSingleDepartment = totalDepartments === 1
  const singleDeptName = isSingleDepartment ? Object.keys(departmentAverages.final)[0] : null

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Building2 className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Department Performance</h2>
          <p className="text-slate-600">Performance analysis by department</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {!isSingleDepartment && (
          <>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-300 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-green-800">Highest Rated</h3>
              </div>
              {highestRatedDepartments.map((departmentName) => (
                <div key={departmentName} className="space-y-3">
                  <p className="text-2xl font-bold text-green-900">{departmentName}</p>
                  <div className="space-y-2">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-green-700 font-semibold">Final Rating</p>
                      <p className="text-xl font-bold text-green-900">
                        {departmentAverages.final[departmentName]?.toFixed(2) ?? "N/A"}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white rounded-lg p-2 text-center">
                        <p className="text-xs text-slate-600 font-semibold">Values</p>
                        <p className="font-bold text-slate-900">
                          {departmentAverages.values[departmentName]?.toFixed(2) ?? "N/A"}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center">
                        <p className="text-xs text-slate-600 font-semibold">Leadership</p>
                        <p className="font-bold text-slate-900">
                          {departmentAverages.leadership[departmentName]?.toFixed(2) ?? "N/A"}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center">
                        <p className="text-xs text-slate-600 font-semibold">Prof.</p>
                        <p className="font-bold text-slate-900">
                          {departmentAverages.template[departmentName]?.toFixed(2) ?? "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border-2 border-red-300 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-red-800">Lowest Rated</h3>
              </div>
              {lowestRatedDepartments.map((departmentName) => (
                <div key={departmentName} className="space-y-3">
                  <p className="text-2xl font-bold text-red-900">{departmentName}</p>
                  <div className="space-y-2">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-red-700 font-semibold">Final Rating</p>
                      <p className="text-xl font-bold text-red-900">
                        {departmentAverages.final[departmentName]?.toFixed(2) ?? "N/A"}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white rounded-lg p-2 text-center">
                        <p className="text-xs text-slate-600 font-semibold">Values</p>
                        <p className="font-bold text-slate-900">
                          {departmentAverages.values[departmentName]?.toFixed(2) ?? "N/A"}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center">
                        <p className="text-xs text-slate-600 font-semibold">Leadership</p>
                        <p className="font-bold text-slate-900">
                          {departmentAverages.leadership[departmentName]?.toFixed(2) ?? "N/A"}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-2 text-center">
                        <p className="text-xs text-slate-600 font-semibold">Prof.</p>
                        <p className="font-bold text-slate-900">
                          {departmentAverages.template[departmentName]?.toFixed(2) ?? "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {isSingleDepartment && (
          <div className="md:col-span-2 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-300 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Award className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-blue-800">Only Department on Record</h3>
            </div>
            {singleDeptName && (
              <div className="space-y-3">
                <p className="text-2xl font-bold text-blue-900">{singleDeptName}</p>
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-blue-700 font-semibold">Final Rating</p>
                    <p className="text-xl font-bold text-blue-900">
                      {departmentAverages.final[singleDeptName]?.toFixed(2) ?? "N/A"}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-600 font-semibold">Values</p>
                      <p className="font-bold text-slate-900">
                        {departmentAverages.values[singleDeptName]?.toFixed(2) ?? "N/A"}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-600 font-semibold">Leadership</p>
                      <p className="font-bold text-slate-900">
                        {departmentAverages.leadership[singleDeptName]?.toFixed(2) ?? "N/A"}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-600 font-semibold">Professional</p>
                      <p className="font-bold text-slate-900">
                        {departmentAverages.template[singleDeptName]?.toFixed(2) ?? "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Award className="h-5 w-5 text-slate-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">All Departments</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Department Name</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Final Rating</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Values Rating</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Leadership Rating</th>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Professional Rating</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(departmentAverages.final)
                .sort(([, a], [, b]) => b - a)
                .map(([departmentName]) => {
                  const finalRating = departmentAverages.final[departmentName] || 0
                  const valuesRating = departmentAverages.values[departmentName] || 0
                  const leadershipRating = departmentAverages.leadership[departmentName] || 0
                  const templateRating = departmentAverages.template[departmentName] || 0

                  const getRatingColor = (rating: number) => {
                    if (rating < 3) return "bg-red-500"
                    if (rating >= 3 && rating < 4) return "bg-yellow-500"
                    return "bg-green-500"
                  }

                  const getRatingLabel = (rating: number) => {
                    if (rating < 3) return "Low Rated"
                    if (rating >= 3 && rating < 4) return "Average"
                    return "High Rated"
                  }

                  return (
                    <tr
                      key={departmentName}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-200"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="font-medium text-slate-800">{departmentName}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <span className="text-lg font-bold text-slate-800">
                            {typeof finalRating === "number" && !isNaN(finalRating) ? finalRating.toFixed(2) : "N/A"}
                          </span>
                          <p className="text-xs text-slate-500">{getRatingLabel(finalRating)}</p>
                          <div className="w-24 bg-slate-200 rounded-full h-2">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${getRatingColor(finalRating)}`}
                              style={{ width: `${Math.min((finalRating / 15) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <span className="text-lg font-bold text-slate-800">
                            {typeof valuesRating === "number" && !isNaN(valuesRating) ? valuesRating.toFixed(2) : "N/A"}
                          </span>
                          <p className="text-xs text-slate-500">{getRatingLabel(valuesRating)}</p>
                          <div className="w-24 bg-slate-200 rounded-full h-2">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${getRatingColor(valuesRating)}`}
                              style={{ width: `${Math.min((valuesRating / 5) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <span className="text-lg font-bold text-slate-800">
                            {typeof leadershipRating === "number" && !isNaN(leadershipRating)
                              ? leadershipRating.toFixed(2)
                              : "N/A"}
                          </span>
                          <p className="text-xs text-slate-500">{getRatingLabel(leadershipRating)}</p>
                          <div className="w-24 bg-slate-200 rounded-full h-2">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${getRatingColor(leadershipRating)}`}
                              style={{ width: `${Math.min((leadershipRating / 5) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <span className="text-lg font-bold text-slate-800">
                            {typeof templateRating === "number" && !isNaN(templateRating)
                              ? templateRating.toFixed(2)
                              : "N/A"}
                          </span>
                          <p className="text-xs text-slate-500">{getRatingLabel(templateRating)}</p>
                          <div className="w-24 bg-slate-200 rounded-full h-2">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${getRatingColor(templateRating)}`}
                              style={{ width: `${Math.min((templateRating / 5) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3 px-2">
          <div className="p-2 bg-slate-100 rounded-lg">
            <Users className="h-5 w-5 text-slate-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Users by Department</h3>
        </div>

        {Object.entries(departmentUserScores).map(([departmentName, users]) => {
          const isExpanded = expandedDepts[departmentName] || false
          const currentPage = pagination[departmentName] || 0
          const totalPages = Math.ceil(users.length / itemsPerPage)
          const paginatedUsers = getPaginatedUsers(users, departmentName)

          return (
            <div
              key={departmentName}
              className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-200"
            >
              <button
                onClick={() => handleToggleExpand(departmentName)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors duration-150"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-lg font-bold text-slate-900">{departmentName}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full">
                        {users.length} Users
                      </span>
                      <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-1 rounded-full">
                        Final Score: {departmentAverages.final[departmentName]?.toFixed(2) ?? "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-slate-600 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                />
              </button>

              {isExpanded && (
                <div className="border-t border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                          <th className="text-left py-3 px-4 font-bold text-slate-900 text-sm">User Name</th>
                          <th className="text-center py-3 px-4 font-bold text-slate-900 text-sm">Values</th>
                          <th className="text-center py-3 px-4 font-bold text-slate-900 text-sm">Leadership</th>
                          <th className="text-center py-3 px-4 font-bold text-slate-900 text-sm">Templates</th>
                          <th className="text-center py-3 px-4 font-bold text-slate-900 text-sm">Combined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.map((user, idx) => {
                          const combinedScore = user.values + user.leadership + user.templates
                          const getScoreColor = (score: number) => {
                            if (score < 1) return "text-red-600 bg-red-50"
                            if (score < 2) return "text-orange-600 bg-orange-50"
                            return "text-green-600 bg-green-50"
                          }

                          return (
                            <tr
                              key={`${departmentName}-${user.userId}-${idx}`}
                              className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-150"
                            >
                              <td className="py-3 px-4">
                                <span className="font-semibold text-slate-900">{user.userName}</span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span
                                  className={`inline-block font-bold px-2 py-1 rounded text-xs ${getScoreColor(user.values)}`}
                                >
                                  {user.values.toFixed(2)}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span
                                  className={`inline-block font-bold px-2 py-1 rounded text-xs ${getScoreColor(user.leadership)}`}
                                >
                                  {user.leadership.toFixed(2)}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span
                                  className={`inline-block font-bold px-2 py-1 rounded text-xs ${getScoreColor(user.templates)}`}
                                >
                                  {user.templates.toFixed(2)}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="inline-block font-bold px-3 py-1 rounded bg-slate-900 text-white text-xs">
                                  {combinedScore.toFixed(2)}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                      <div className="text-sm font-medium text-slate-600">
                        Page {currentPage + 1} of {totalPages}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePrevPage(departmentName)}
                          disabled={currentPage === 0}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 font-medium text-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Prev
                        </button>
                        <button
                          onClick={() => handleNextPage(departmentName, users.length)}
                          disabled={currentPage >= totalPages - 1}
                          className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 font-medium text-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
})

export default Department
